import FlagFR from '@monofront/design-system/visuals/Icons/Old/FlagFR';
import New from '@monofront/design-system/visuals/Icons/Old/New';
import UltraFresh from '@monofront/design-system/visuals/Icons/Old/UltraFresh';
import { IMeta } from '@monofront/types';
import {
  getBundleProductDetails,
  getIsNew,
  getIsOriginFranceFromTags,
} from '@monofront/business/utils';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import Tag from '@monofront/design-system/components/Tag';
import { useTranslations } from 'next-intl';

interface IFigureProps {
  handle: string;
  tags?: string[];
  img?: string;
  inventoryQuantity?: number;
  meta?: IMeta;
  recupTag?: string;
  quality?: number;
  title: string;
  isOnClickDisabled?: boolean;
  isUltraFresh?: boolean;
}

const ProductImage: React.FC<IFigureProps> = ({
  title,
  tags = [],
  img,
  inventoryQuantity,
  recupTag,
  quality = 75,
  isOnClickDisabled = false,
  isUltraFresh = false,
}) => {
  const isOriginFrance = getIsOriginFranceFromTags(tags);
  const isNew = getIsNew(tags);
  const tCatalog = useTranslations('Catalog');

  const { productBundleRate } = getBundleProductDetails(tags!);

  return (
    <>
      <figure className="product-img">
        <ResponsiveImage
          src={img!}
          alt={title}
          width={1024}
          height={1024}
          quality={quality}
          objectFit="contain"
        />

        {(isOriginFrance || isUltraFresh) && (
          <div className="icons">
            {isOriginFrance && (
              <span className="fr">
                <FlagFR />
              </span>
            )}
            {isUltraFresh && (
              <span className="ultrafresh">
                <UltraFresh />
              </span>
            )}
          </div>
        )}
        <div className="bottom">
          {recupTag && inventoryQuantity && inventoryQuantity > 0 ? (
            <div className="recup">
              <Tag variant="secondary">{recupTag}</Tag>
            </div>
          ) : (
            isNew && <New />
          )}
          {typeof inventoryQuantity === 'number' && inventoryQuantity <= 0 ? (
            <div className="not-available">
              <Tag variant="danger-light">
                {recupTag ? tCatalog('endOfStock') : tCatalog('comesBackSoon')}
              </Tag>
            </div>
          ) : null}
        </div>
      </figure>
      <style jsx>{`
        .product-img {
          position: relative;
          display: flex;
          flex-direction: column;
          cursor: ${isOnClickDisabled ? 'default' : 'pointer'};
          height: 100%;
        }

        .discount-price {
          position: absolute;
          bottom: var(--space-2);
          left: var(--space-2);
        }

        .icons {
          position: absolute;
          top: var(--space-2);
          left: var(--space-2);
        }

        .ultrafresh {
          left: ${isOriginFrance
            ? 'var(--negative-space-4)'
            : 'var(--space-7)'};
        }

        .multiple {
          display: 'flex';
          font-size: var(--font-size-2);
          background-color: var(--clr-tertiary-2);
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          border-radius: 50%;
          font-weight: var(--font-weight-bold);
          margin-left: var(--space-1);
        }

        .bottom {
          position: absolute;
          justify-content: space-between;
          bottom: var(--space-4);
          left: ${recupTag && inventoryQuantity && inventoryQuantity > 0
            ? '-var(--space-2)'
            : recupTag && inventoryQuantity && inventoryQuantity > 0
            ? 'var(--space-2);'
            : isNew
            ? 'var(--space-2);'
            : 'initial'};
          display: flex;
          right: var(--space-2);
          align-items: flex-end;
        }

        .not-available > :global(div) {
          padding: var(--space-1) var(--space-2);
          font-size: var(--font-size-2);
          text-align: center;
          border-radius: var(--rd-3);
        }

        .recup > :global(*),
        .not-available > :global(*) {
          padding-left: var(--space-2);
          padding-right: var(--space-2);
        }

        .recup :global(div) {
          white-space: ${productBundleRate ? 'nowrap' : 'normal'};
          font-size: ${productBundleRate ? 'var(--font-size-1)' : 'inherit'};
        }

        @media (max-width: 800px) {
          .bottom {
            bottom: var(--space-4);
          }

          .not-available > :global(*) {
            white-space: pre-line;
          }
        }

        @media (max-width: 375px) {
          .bundle-product :global(.text-nb-products) {
            white-space: normal;
          }
        }

        @media (max-width: 359px) {
          .bottom {
            display: none;
          }
        }
      `}</style>
    </>
  );
};

export default ProductImage;
